import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Divider, ResponsiveTableRow } from '@fs/zion-ui'
import { css } from '@emotion/core'
import { DmcResponsiveTable } from '../../common/DmcResponsiveTable'
import ObjectView from '../../common/ObjectView'
import { actions, transformContributions } from './useUserContributions'
import { timestampToDateString } from '../../../util/DateUtil'
import { useInfiniteScroll } from '../../../hooks/useInfiniteScroll'
import { deepCopy, isNullOrUndefinedOrEmpty } from '../../../util/Utils'
import { GroupLink } from '../../app/Links'
import { getUserSubContributions } from '../../../services/userService'
import useNotifications from '../../../hooks/useNotifications'
import { DmcText } from '../../common/DmcText'

const wrapit = css`
  overflow-wrap: break-word;
`
const labelCss = css`
  button {
    background: var(--gray02a);
  }
  button:not(:disabled):focus:not(:focus-visible):not(:hover) {
    background: var(--gray02a);
  }
`
const personIdFields = [
  'personId',
  'person1Id',
  'person2Id',
  'spouse1Id',
  'spouse2Id',
  'parent1Id',
  'parent2Id',
  'childId',
]

export default function UserContributionsTable({
  pageState,
  getContributions,
  rerenderInfiniteScroll,
  setRerenderInfiniteScroll,
}) {
  const containerRef = useRef()
  const triggerRef = useRef()

  return (
    <>
      <div ref={containerRef}>
        <UserContributionsTableBody
          containerRef={containerRef}
          triggerRef={triggerRef}
          pageState={pageState}
          getContributions={getContributions}
          rerenderInfiniteScroll={rerenderInfiniteScroll}
          setRerenderInfiniteScroll={setRerenderInfiniteScroll}
        />
      </div>
      <div ref={triggerRef} data-testid="triggerRef">
        {' '}
      </div>
    </>
  )
}

function UserContributionsTableBody({
  pageState,
  getContributions,
  containerRef,
  triggerRef,
  rerenderInfiniteScroll,
  setRerenderInfiniteScroll,
}) {
  const { t } = useTranslation()
  const { error } = useNotifications()
  const infiniteScrollingCallbackRef = useRef()
  const [subContributionsObj, setSubContributionsObj] = useState({})
  // everytime this component is loaded, "infiniteScrollingCallbackRef.current" is getting
  // a new function with the updated "pageState"
  infiniteScrollingCallbackRef.current = () => {
    if (isNullOrUndefinedOrEmpty(pageState?.metadata?.nextPageToken)) {
      return
    }
    getContributions(
      pageState.currentUser.contributorId,
      pageState.metadata.nextPageToken,
      null,
      pageState.metadata.type,
      pageState.metadata.subtypes,
      actions.CONCAT_CONTRIBUTIONS
    )
  }
  useInfiniteScroll(
    containerRef,
    triggerRef,
    infiniteScrollingCallbackRef,
    IntersectionObserver,
    ResizeObserver,
    rerenderInfiniteScroll,
    setRerenderInfiniteScroll
  )

  const getGroupId = (contribution) => {
    const personType = Object.keys(contribution).find((key) => {
      return personIdFields.includes(key)
    })
    const person = pageState.persons.find((p) => contribution[personType] === p.personId)
    return person?.groupId
  }
  const getSubContributions = async (userId, commandId) => {
    await getUserSubContributions(userId, commandId)
      .then((response) => {
        setSubContributionsObj((prevState) => {
          const newState = deepCopy(prevState)
          newState[commandId] = transformContributions(response.contributions)
          return newState
        })
      })
      .catch((err) => {
        error(t('user.getSubContributions.error', 'Error retrieving subcontributions for this contribution'), err)
      })
  }

  function getConclusionInformation(conclusion, changesFromOtherConclusion) {
    const base = {}

    if (conclusion.value && changesFromOtherConclusion) {
      Object.keys({ ...conclusion.value, ...changesFromOtherConclusion })
        .sort()
        .forEach((key) => {
          base[key] = ''
        })
    }

    const changes = { ...base, ...conclusion.value }

    return {
      modified: conclusion.attribution?.modifiedDateNormalized?.[0]?.value,
      contributor: conclusion.attribution?.contributor?.id,
      submitter: conclusion.attribution?.submitter?.id,
      ...changes,
    }
  }

  return (
    <DmcResponsiveTable
      id="UserContributionsTable"
      totalColumnCount={3}
      atSizeColumns={{
        default: {
          labels: [t('user.contribution', 'Contribution'), t('action.change', 'Change'), t('operations', 'Operations')],
          columnWidths: [3, 8, 1],
        },
      }}
      separator={<br />}
    >
      {pageState.contributions.map((contribution) => {
        const groupId = getGroupId(contribution.contribution)
        let subContributions
        if (subContributionsObj[contribution.contribution.commandId]) {
          subContributions = subContributionsObj[contribution.contribution.commandId]
        } else if (contribution.hasSubChanges) {
          subContributions = []
        }
        return (
          <ResponsiveTableRow
            id={`${contribution.contribution.type}${contribution.modified}`}
            key={`${contribution.contribution.type}${contribution.modified}`}
            column1={
              <span css={wrapit}>
                {contribution.contributionType}
                {groupId && (
                  <>
                    <br />
                    {t('groupId', 'Group ID')}: <GroupLink groupId={groupId} />
                  </>
                )}
                <br />
                {timestampToDateString({
                  timestamp: contribution.modified,
                  withTime: true,
                })}
              </span>
            }
            column2={
              <span>
                <ObjectView
                  object={contribution.contribution}
                  metadata={{ persons: pageState.metadata.persons }}
                  expanderDetails={{
                    dataList: subContributions,
                    label: (
                      <DmcText
                        id="subLevelContribExpander"
                        bold
                        text={t('user.contribution.subLevelContributions', 'Sub-Level Contributions')}
                      />
                    ),
                    labelStyle: labelCss,
                    expandCallbackFn: () =>
                      getSubContributions(contribution.contribution.contributorId, contribution.contribution.commandId),
                  }}
                  fieldsToOmit={[
                    'commandId',
                    'changeId',
                    'previousEntityRef',
                    'resultingEntityRef',
                    'previousConclusion',
                    'resultingConclusion',
                    'fieldsUpdated',
                  ]}
                />
                {contribution.contribution.previousConclusion && (
                  <>
                    <Divider label={t('user.contributions.previousConclusion', 'Previous Conclusion')} />
                    <ObjectView
                      object={getConclusionInformation(
                        contribution.contribution.previousConclusion,
                        contribution.contribution.resultingConclusion?.value
                      )}
                      fieldsToOmit={['type']}
                    />
                  </>
                )}
                {contribution.contribution.resultingConclusion && (
                  <>
                    <Divider label={t('user.contributions.resultingConclusion', 'Resulting Conclusion')} />
                    <ObjectView
                      object={getConclusionInformation(
                        contribution.contribution.resultingConclusion,
                        contribution.contribution.previousConclusion?.value
                      )}
                      fieldsToOmit={['type']}
                    />
                  </>
                )}
              </span>
            }
          />
        )
      })}
    </DmcResponsiveTable>
  )
}
